import React, { Fragment } from "react";
import PropTypes from "prop-types";
import { always } from "ramda";
import styled from "styled-components";
import {
  Button,
  IconMinusCircleFilled,
  IconPlus,
  InputField,
  INPUTFIELD_LABEL_TYPES,
  INPUTFIELD_TYPES,
  INPUTFIELD_VALIDATION_TYPES,
  Text,
  THEME,
} from "../../../migration-wrappers";
import FF from "../../../../utils/functional/form-field";

// #region Component Styles
const AddThresholdButton = styled(Button).attrs({
  variant: Button.TEXT,
})`
  padding: 0;
`;

const IconAdd = styled(IconPlus)`
  width: 10px;
  height: 10px;
  margin-right: 3px;
`;

const Icon = styled.svg`
  margin-right: 5px;
  width: 15px;
`;

const Title = styled(Text).attrs({ isParagraph: true })`
  p {
    font-weight: 600;
    margin: 0;
    margin-bottom: ${THEME.getUnit("small")};
  }
`;

const ThresholdEditContainer = styled.div`
  display: flex;
  flex: 1;
`;

const ThresholdInputContainer = styled.div`
  max-width: 200px;
`;

const ThresholdInput = styled(InputField).attrs({
  type: INPUTFIELD_TYPES.NUMBER,
})`
  display: block;
  margin-bottom: ${THEME.getUnit("regular")};

  input {
    width: 200px;
  }

  :last-of-type {
    margin-bottom: 0;
  }
`;

const ThresholdParenthesis = styled.div`
  width: 20px;
  border-bottom-right-radius: ${THEME.getRadius()};
  border-top-right-radius: ${THEME.getRadius()};
  border: solid 1px ${THEME.getGrayColor("400")};
  border-left: 0;
  margin: 0 ${THEME.getUnit("small")};
`;

const ThresholdRemoveButton = styled(Button).attrs({
  title: "Remove Thresholds",
})`
  align-items: center;
  align-self: center;
  padding: ${THEME.getUnit("small")};
  display: flex;
  padding: ${THEME.getUnit("small")};
`;

const getValidation = FF.case({
  invalid: (_, { feedbackMessageId }) => ({
    message: feedbackMessageId,
    type: INPUTFIELD_VALIDATION_TYPES.INVALID,
  }),
  _: always(undefined),
});

const Threshold = ({
  threshold,
  setBusinessUnitThreshold,
  setBusinessUnitThresholdEnabled,
}) => {
  const { fields } = threshold;

  if (!threshold?.isEnabled) {
    return (
      <AddThresholdButton onClick={() => setBusinessUnitThresholdEnabled(true)}>
        <IconAdd />
        Add Thresholds
      </AddThresholdButton>
    );
  }

  const getInputField = (field, label, ref) => (
    <ThresholdInput
      ref={ref}
      onChange={(e) => {
        setBusinessUnitThreshold(FF.getMeta(field).id, e.target.value);
      }}
      validation={getValidation(field)}
      value={FF.getValue(field)}
      variant={{
        type: INPUTFIELD_LABEL_TYPES.STATIC,
        label,
        placeholder: "Add minimum volume",
      }}
      key={FF.getMeta(field).id}
    />
  );

  return (
    <Fragment>
      <Title>Thresholds</Title>
      <ThresholdEditContainer>
        <ThresholdInputContainer>
          {getInputField(fields.daily, "Daily")}
          {getInputField(fields.weekly, "Weekly")}
          {getInputField(fields.monthly, "Monthly")}
          {getInputField(fields.yearly, "Yearly")}
        </ThresholdInputContainer>

        <ThresholdParenthesis />

        <ThresholdRemoveButton
          onClick={() => setBusinessUnitThresholdEnabled(false)}
          variant={Button.SECONDARY}
        >
          <Icon as={IconMinusCircleFilled} />
          Remove
        </ThresholdRemoveButton>
      </ThresholdEditContainer>
    </Fragment>
  );
};

Threshold.propTypes = {
  setBusinessUnitThreshold: PropTypes.func.isRequired,
  setBusinessUnitThresholdEnabled: PropTypes.func.isRequired,
  threshold: PropTypes.shape({
    fields: PropTypes.object,
    isEnabled: PropTypes.bool.isRequired,
  }),
};

export default Threshold;
